import Box from "@/elements/Box";
import Typography from "@/elements/Typography";
import Loading from "../Loading";
import LoadingError from "../LoadingError";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "@/elements/Table";
import { useAuth } from "@/context/useAuth";
import Button from "@/elements/Button";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
} from "@mui/material";
import ClienteDialog from "../ClienteDialog";
import { useCallback, useMemo, useState } from "react";
import { useConfirmationDialog } from "@/utils/useConfirmationDialog";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import {
  ModelloDispositivoDto,
  getGetModelliDispositivoQueryKey,
  useDeleteModelloDispositivo,
  useGetModelliDispositivo,
} from "@/api";
import ModelloDispositivoDialog from "../ModelloDispositivoDialog";
import { AxiosError } from "axios";

const columnHelper = createColumnHelper<ModelloDispositivoDto>();

function ModelliDispositivoPage() {
  const { hasPermission } = useAuth();
  const { isLoading, error, data } = useGetModelliDispositivo();

  const [modelliDialogState, setModelliDialogState] = useState({
    open: false,
    modello: null as ModelloDispositivoDto | null,
  });

  const [requestError, setRequestError] = useState<AxiosError | null>(null);

  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  const { mutateAsync } = useDeleteModelloDispositivo({
    mutation: {
      onSuccess: () => {
        toast.success("Modello eliminato!");
      },
      onError: () => {
        toast.error("Errore nella richiesta");
      },
    },
  });

  const queryClient = useQueryClient();

  const deleteModello = useCallback(
    async (modelloId: number) => {
      try {
        await mutateAsync({ id: modelloId });
        queryClient.invalidateQueries({
          queryKey: getGetModelliDispositivoQueryKey(),
        });
        // queryClient.invalidateQueries({ queryKey: getGetClientiQueryKey() });
      } catch (err) {
        const axiosError = err as AxiosError;
        if (axiosError) {
          setRequestError(axiosError);
        }
      }
    },
    [mutateAsync, queryClient]
  );

  const columns = useMemo(() => {
    const columns = [
      columnHelper.accessor("imageUrl", {
        header: "Immagine",
        size: undefined,
        cell: ({ row }) => {
          return (
            row.original.imageUrl && (
              <img
                src={row.original.imageUrl}
                alt={row.original.image?.fileName}
                style={{ width: 100 }}
              />
            )
          );
        },
      }),
      columnHelper.accessor("modello", {
        header: "Nome Modello",
        size: undefined,
      }),
      columnHelper.accessor("maschera", {
        header: "Maschera registri",
        size: undefined,
      }),
      columnHelper.accessor("tipoDispositivo", {
        header: "Tipo dispositivo",
        size: undefined,
      }),
      columnHelper.accessor("mappaturaAllarmi.type", {
        header: "Mappatura allarme",
        size: undefined,
      }),
      hasPermission("ModelliDispositivoChange") &&
        columnHelper.display({
          id: "buttons",
          header: "",
          size: 250,
          maxSize: 250,
          cell: ({ row }) => {
            return (
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  startIcon={<Icon>edit</Icon>}
                  onClick={() => {
                    setModelliDialogState({
                      open: true,
                      modello: row.original,
                    });
                  }}
                >
                  Modifica
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<Icon>delete_outlined</Icon>}
                  onClick={() => {
                    askConfirmation({
                      title: "Elimina modello",
                      message: `Sei sicuro di voler eliminare il modello ${row.original.modello}?`,
                      okColor: "error",
                      onConfirm: () => {
                        return deleteModello(row.original.id);
                      },
                    });
                  }}
                >
                  Elimina
                </Button>
              </Box>
            );
          },
        }),
    ].filter((x) => x);
    return columns as unknown as Exclude<(typeof columns)[number], boolean>[];
  }, [setModelliDialogState, askConfirmation, deleteModello, hasPermission]);

  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return <LoadingError error={error} />;
  }

  const clienti = data.data || [];

  return (
    <div>
      <Box p={2}>
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h4" mr={2}>
            Modelli
          </Typography>
          <Box display="flex" gap={1} pt={1} flexWrap="wrap">
            {hasPermission("UsersEsterniChange") && (
              <Button
                size="small"
                color="light"
                onClick={() => {
                  setModelliDialogState({
                    open: true,
                    modello: null,
                  });
                }}
              >
                Aggiungi modello
              </Button>
            )}
          </Box>
        </Box>

        <Table
          columns={columns}
          data={clienti}
          sortable={true}
          initialSort={[{ id: "modello", desc: false }]}
        />

        <ModelloDispositivoDialog
          open={modelliDialogState.open}
          modello={modelliDialogState.modello}
          onClose={() => {
            setModelliDialogState((x) => ({
              ...x,
              open: false,
            }));
          }}
        />
        {confirmationDialog}
      </Box>
      {/* Dialog */}
      <Dialog
        open={requestError !== null}
        onClose={() => setRequestError(null)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <h2>Errore</h2>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {(requestError?.response?.data as string) ||
              "Errore nella richiesta"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestError(null)} color="error">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ModelliDispositivoPage;
